import React, {
  Fragment,
  useEffect,
  useState,
  useContext,
  useRef,
} from 'react';
import {
  getDangerProducts,
  getFilterOptions,
  getCategoryFilter,
} from '../../core/services';
import ResponsivePagination from 'react-responsive-pagination';

import { useLocation } from 'react-router-dom';
import { AppContext } from '../../core/Contexts/AppContext';
import CardDp from '../../component/Card/CardDp';
import { DangerProductFilter } from '../../component/Section/DangerProductFilter';

const DANGER_PRODUCTS_RETURN_KEY = 'danger-products-return-state';

const getSavedDangerProductsState = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem(DANGER_PRODUCTS_RETURN_KEY) || 'null',
    );
  } catch (error) {
    return null;
  }
};

const DangerProducts = () => {
  const [filters, setFilters] = useState({});
  const [products, setProducts] = useState([]);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const location = useLocation();
  const restoreStateRef = useRef(getSavedDangerProductsState());
  const [page, setPage] = useState(() => {
    const savedPage = Number(restoreStateRef.current?.page);
    return Number.isFinite(savedPage) && savedPage > 0 ? savedPage : 1;
  });

  const { setTitle, setHeaderType, setImage } = useContext(AppContext);

  const [filtersOptions, setFiltersOptions] = useState({
    categories: [],
    countries: [],
    risks: [],
    danger_levels: [],
    product_filters: [],
  });
  const [isCategoryBarVisible, setIsCategoryBarVisible] = useState(false);

  useEffect(() => {
    const currentUrl = `${location.pathname}${location.search}`;

    if (
      restoreStateRef.current?.returnTo &&
      restoreStateRef.current.returnTo !== currentUrl
    ) {
      restoreStateRef.current = null;
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    void getDangerProducts(page, filters).then((result) => {
      setProducts(result.data);
      setTotalPageCount(result.last_page);

      window.requestAnimationFrame(() => {
        const restoreState = restoreStateRef.current;

        if (Number(restoreState?.page) === Number(page)) {
          const productElement = restoreState?.productId
            ? document.getElementById(
                `danger-product-${restoreState.productId}`,
              )
            : null;

          if (productElement) {
            productElement.scrollIntoView({
              behavior: 'auto',
              block: 'center',
            });
          } else {
            window.scrollTo({
              top: restoreState?.scrollY ?? 0,
              behavior: 'auto',
            });
          }

          restoreStateRef.current = null;
          sessionStorage.removeItem(DANGER_PRODUCTS_RETURN_KEY);
          return;
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }, [page, filters]);

  useEffect(() => {
    setTitle('საფრთხის შემცველი პროდუქტები');
    setImage('../../images/hero-images/pattern-bg-optimized.jpg');
    setHeaderType(1);

    void getFilterOptions()
      .then((options) => {
        setFiltersOptions({ ...options });
      })
      .catch((error) => {
        console.error('Error fetching filters:', error);
      });
  }, [setHeaderType, setImage, setTitle]);

  useEffect(() => {
    if (filters.search_category) {
      void getCategoryFilter(filters.search_category)
        .then((options) => {
          setFiltersOptions((prev) => {
            return {
              ...prev,
              product_filters: options,
            };
          });
        })
        .catch((error) => {
          console.error('Error fetching filters:', error);
        });
    } else {
      setFiltersOptions((prev) => ({ ...prev, product_filters: [] }));
    }
  }, [filters.search_category]);

  const handleFilterChange = (prop, name) => {
    setPage(1);

    if (prop.target?.value) {
      setFilters((prev) => ({ ...prev, [name]: prop.target.value }));
    } else {
      if (Array.isArray(prop)) {
        setFilters((prev) => ({ ...prev, [name]: prop.map((x) => x.value) }));
      } else {
        setFilters((prev) => ({ ...prev, [name]: prop.value }));
      }
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setPage(1);
    setFilters((prev) => {
      const next = { ...prev };
      const isSame = String(next.search_category ?? '') === String(categoryId);

      if (isSame) {
        delete next.search_category;
        delete next.search_sub_product;
      } else {
        next.search_category = categoryId;
        delete next.search_sub_product;
      }

      return next;
    });
  };

  return (
    <Fragment>
      <div className='news_container'>
        <section className='container padTop-0'>
          <button
            type='button'
            className='btn dp-category-toggle d-md-none mb-2'
            onClick={() => setIsCategoryBarVisible((prev) => !prev)}
          >
            სორტირება {isCategoryBarVisible ? '▲' : '▼'}
          </button>

          <div
            className={`dp-category-bar ${
              isCategoryBarVisible ? 'dp-category-bar--visible' : ''
            }`}
          >
            {filtersOptions.categories?.map((cat) => {
              const isActive =
                String(filters.search_category ?? '') === String(cat.value);

              return (
                <button
                  key={cat.value}
                  type='button'
                  className={`btn dp-category-btn ${
                    isActive ? 'dp-category-btn--active' : ''
                  }`}
                  onClick={() => handleCategoryToggle(cat.value)}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          <button
            type='button'
            className='btn dp-filter-toggle d-md-none mb-2'
            onClick={() => setIsFilterVisible(!isFilterVisible)}
          >
            ფილტრი {isFilterVisible ? '▲' : '▼'}
          </button>

          <button
            className='d-none d-md-block ml-auto semi-bold font__size--14 text__14-1024 btn btn__outlined--blue color__blue no__opacity rounded-2 shadow'
            onClick={() => setIsFilterVisible(!isFilterVisible)}
          >
            {isFilterVisible ? 'ფილტრის გათიშვა' : 'ფილტრის ჩვენება'}
          </button>

          {isFilterVisible && (
            <DangerProductFilter
              filtersOptions={filtersOptions}
              filterChanged={handleFilterChange}
            />
          )}
          <div className='row mt-3'>
            {products.length > 0 ? (
              products.map((product) => (
                <div
                  key={product.id}
                  id={`danger-product-${product.id}`}
                  className='col-lg-4 mb-4'
                >
                  <CardDp
                    data={product}
                    currentPath={location.pathname}
                    currentSearch={location.search}
                    currentPage={page}
                  />
                </div>
              ))
            ) : (
              <div className='col-12 text-center'>
                <p className='alert alert-warning'>
                  ვერ მოიძებნა საფრთხის შემცველი პროდუქტი.
                </p>
              </div>
            )}
          </div>
          {totalPageCount > 1 && (
            <ResponsivePagination
              current={page}
              total={totalPageCount}
              onPageChange={setPage}
              maxWidth={640}
            />
          )}
        </section>
      </div>
    </Fragment>
  );
};

export default DangerProducts;
